import { useState } from 'react';
import { useAppConfigContext } from '@/context/AppConfig';
import { useTuxedo } from '@/reports/index';

export const mortyScriptUrl =
  'https://morty-embed.s3.us-east-2.amazonaws.com/iq.js';

type MortyInit = {
  customerId: string;
  signature: string;
  timestamp: string;
  apiKey: string;
};

interface CustomWindow extends Window {
  Morty?: {
    init: ({ customerId, signature, timestamp, apiKey }: MortyInit) => void;
  };
}

export const useLoadScript = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isMortyWidgetLoaded, setIsMortyWidgetLoaded] = useState(false);

  const { logError } = useAppConfigContext();

  const { prepMortyiFrame } = useTuxedo();

  const loadScript = () => {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = mortyScriptUrl;
      document.body.appendChild(script);

      script.addEventListener('load', () => {
        setIsLoaded(true);
        resolve(script);
      });

      script.addEventListener('error', () => {
        reject(new Error(`${mortyScriptUrl} failed to load.`));
      });
    });
  };

  const load = async () => {
    const isMortyScriptLoaded = document.querySelector(
      `script[src="${mortyScriptUrl}"]`
    );

    if (!isLoaded && !isMortyScriptLoaded) {
      try {
        await loadScript();
      } catch (e: unknown) {
        if (e instanceof Error) {
          logError('Error loading morty script', e);
        }
      }
    }
  };

  const startMorty = async (customerId: string) => {
    const mortyPrep = await prepMortyiFrame.mutateAsync(customerId);

    (window as CustomWindow).Morty?.init({
      customerId,
      signature: mortyPrep?.sig_hash,
      timestamp: mortyPrep?.sig_time,
      apiKey: mortyPrep?.api_key,
    });

    setIsMortyWidgetLoaded(true);
  };

  return {
    load,
    isLoaded,
    isMortyWidgetLoaded,
    startMorty,
  };
};
